import {
    AlertCircle,
    CheckCircle,
    ChevronDown,
    ChevronRight,
    Crown,
    Database,
    Edit,
    Eye,
    Filter,
    Loader2,
    MoreHorizontal,
    Plus,
    Search,
    Settings,
    Sparkles,
    Trash2,
    XCircle,
    Zap
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { AddonPacksManager } from "../components/admin/AddonPacksManager";
import { AdminSidebar, AdminTab } from "@foundation/components/admin/AdminSidebar";
import { TemplatesSection } from "@/foundation-overrides/components/settings/TemplatesSection";
import { AiSkillsSection } from "../components/settings/AiSkillsSection";
import { PolicyFeedbackPage } from "@/features/revisr/pages/admin/PolicyFeedbackPage";
import { DocumentRegisterSignupsPage } from "@/features/revisr/pages/admin/DocumentRegisterSignupsPage";
import { ActivityLogPage } from "@/features/revisr/pages/admin/ActivityLogPage";
import AssignBasesPage from "../components/admin/AssignBasesPage";
import { GlobalIntegrationsPage } from "../components/admin/GlobalIntegrationsPage";
import { PlanTierForm } from "../components/admin/PlanTierForm";
import { PlanTierViewer } from "../components/admin/PlanTierViewer";
import { RoleManagement } from "../components/admin/RoleManagement/RoleManagement";
import TeableConfigPage from "../components/admin/TeableConfigPage";
import UserManagement from "../components/admin/UserManagement";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
// Table imports removed - using card-based layout for plan tiers
import { useToast } from "../hooks/use-toast";
import {
    AdminService,
    PaginationParams,
    PlanTierFilters,
} from "../services/adminService";
import { UserManagementService } from "../services/userManagementService";
import { PlanTier, ResourceType } from "../types/subscription";
import { ADMIN_CONFIG, ADMIN_TOAST_MESSAGES } from "../utils/constants";

const AdminPortal: React.FC = () => {
  // Tab management
  const [activeTab, setActiveTab] = useState<AdminTab>("subscriptions");
  const [pendingUsersCount, setPendingUsersCount] = useState(0);

  // Subscription management states
  const [planTiers, setPlanTiers] = useState<PlanTier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });

  // Resource type states
  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [showRtForm, setShowRtForm] = useState(false);
  const [rtForm, setRtForm] = useState({ code: "", name: "", description: "", unit_label: "", value_type: "count" as "count" | "flag" | "link" | "plugin" });
  const [rtSaving, setRtSaving] = useState(false);

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState<PlanTier | null>(null);
  const [viewingPlan, setViewingPlan] = useState<PlanTier | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Filter and pagination states
  const [filters, setFilters] = useState<PlanTierFilters>({});
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE,
  });
  const [searchTerm, setSearchTerm] = useState("");

  const { toast } = useToast();

  // Load subscriptions data only when filters/pagination change, not when switching tabs
  useEffect(() => {
    if (activeTab === "subscriptions") {
      loadPlanTiers();
      loadStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination]);

  // Load subscriptions data when first switching to subscriptions tab
  useEffect(() => {
    if (activeTab === "subscriptions" && planTiers.length === 0 && !isLoading) {
      loadPlanTiers();
      loadStats();
    }
    if (activeTab === "subscriptions" && resourceTypes.length === 0) {
      AdminService.getResourceTypes().then(({ data }) => setResourceTypes(data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Load pending users count only once on mount
  useEffect(() => {
    loadPendingUsersCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPendingUsersCount = async () => {
    try {
      const result = await UserManagementService.getPendingUsersCount();
      if (!result.error) {
        setPendingUsersCount(result.count);
      }
    } catch (error) {
      console.error("Failed to load pending users count:", error);
    }
  };

  const loadPlanTiers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await AdminService.getPlanTiers(filters, pagination);

      if (result.error) {
        setError(result.error);
      } else {
        setPlanTiers(result.data);
        setTotalCount(result.count);
      }
    } catch (error) {
      setError("Failed to load plan tiers");
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await AdminService.getPlanTierStats();
      if (result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: keyof PlanTierFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSaveResourceType = async () => {
    const code = rtForm.code.trim().toLowerCase().replace(/\s+/g, "_");
    if (!code || !rtForm.name.trim() || !rtForm.unit_label.trim()) return;
    setRtSaving(true);
    const { data, error: rtError } = await AdminService.createResourceType({
      code,
      name: rtForm.name.trim(),
      description: rtForm.description.trim() || undefined,
      unit_label: rtForm.unit_label.trim(),
      value_type: rtForm.value_type,
    });
    setRtSaving(false);
    if (rtError) {
      toast({ title: "Error", description: rtError, variant: "destructive" });
      return;
    }
    if (data) setResourceTypes((prev) => [...prev, data]);
    setRtForm({ code: "", name: "", description: "", unit_label: "", value_type: "count" });
    setShowRtForm(false);
    toast({ title: "Success", description: "Resource type created" });
  };

  const handlePlanSaved = (planTier: PlanTier) => {
    setShowCreateForm(false);
    setEditingPlan(null);
    loadPlanTiers();
    loadStats();

    toast({
      title: "Success",
      description: editingPlan
        ? ADMIN_TOAST_MESSAGES.PLAN_UPDATED
        : ADMIN_TOAST_MESSAGES.PLAN_CREATED,
    });
  };

  const handleDeletePlan = async (planTier: PlanTier) => {
    if (
      !confirm(
        `Are you sure you want to delete "${planTier.name}"? This will set it as inactive.`
      )
    ) {
      return;
    }

    try {
      const result = await AdminService.deletePlanTier(planTier.id);

      if (result.error) {
        toast({
          title: "Error",
          description: result.error,
          variant: "destructive",
        });
      } else {
        loadPlanTiers();
        loadStats();
        toast({
          title: "Success",
          description: ADMIN_TOAST_MESSAGES.PLAN_DELETED,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: ADMIN_TOAST_MESSAGES.PLAN_DELETE_ERROR,
        variant: "destructive",
      });
    }
  };

  const toggleRowExpansion = (planId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(planId)) {
        newSet.delete(planId);
      } else {
        newSet.add(planId);
      }
      return newSet;
    });
  };

  const formatFeatureValue = (value: unknown): string => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return value === -1 ? "Unlimited" : Number(value).toLocaleString();
    return String(value);
  };

  const formatKeyLabel = (key: string): string => {
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getTierIcon = (level: number) => {
    if (level >= 2) return <Crown className="h-5 w-5 text-amber-500" />;
    if (level === 1) return <Zap className="h-5 w-5 text-blue-500" />;
    return <Sparkles className="h-5 w-5 text-gray-400" />;
  };

  const getTierAccent = (level: number) => {
    if (level >= 2) return { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-700", badge: "bg-amber-100 text-amber-800" };
    if (level === 1) return { border: "border-blue-200", bg: "bg-blue-50", text: "text-blue-700", badge: "bg-blue-100 text-blue-800" };
    return { border: "border-gray-200", bg: "bg-gray-50", text: "text-gray-600", badge: "bg-gray-100 text-gray-700" };
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const totalPages = Math.ceil(
    totalCount / (pagination.limit || ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE)
  );

  const renderSubscriptionManagement = () => (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Subscription Plans</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your plan tiers, features, and pricing</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create Plan
        </Button>
      </div>

      {/* Resource Types */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="h-4 w-4 text-emerald-500" />
              Resource Types
              <Badge variant="outline" className="text-xs font-normal">{resourceTypes.length} registered</Badge>
            </CardTitle>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowRtForm(true)}>
              <Plus className="h-3.5 w-3.5" /> Add Type
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Trackable resources for subscription enforcement. Each type maps to a quota in plan features.
          </p>
        </CardHeader>
        <CardContent>
          {resourceTypes.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No resource types yet. Add one to get started.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {resourceTypes.map((rt) => (
                <div key={rt.id} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/20">
                  <div className="h-8 w-8 rounded-md bg-emerald-100 flex items-center justify-center shrink-0">
                    <Database className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{rt.name}</p>
                    {rt.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{rt.description}</p>}
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant="secondary" className="text-xs">{rt.unit_label}</Badge>
                      <Badge variant="outline" className={`text-xs ${
                        rt.value_type === 'count' ? 'border-emerald-400 text-emerald-700' :
                        rt.value_type === 'flag' ? 'border-blue-400 text-blue-700' :
                        rt.value_type === 'link' ? 'border-purple-400 text-purple-700' :
                        'border-orange-400 text-orange-700'
                      }`}>{rt.value_type}</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Resource Type Dialog */}
      <Dialog open={showRtForm} onOpenChange={(open) => { if (!open) { setShowRtForm(false); setRtForm({ code: "", name: "", description: "", unit_label: "", value_type: "count" as const }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Resource Type</DialogTitle>
            <DialogDescription>
              Define a new trackable resource. Once created, it appears as a quota row in all plan editors.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Display Name</Label>
              <Input
                placeholder="e.g. Policy Documents"
                value={rtForm.name}
                onChange={(e) => setRtForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Code <span className="text-xs text-muted-foreground">(snake_case — used as features JSON key)</span></Label>
              <Input
                placeholder="e.g. altzero_final_policies"
                value={rtForm.code}
                onChange={(e) => setRtForm((p) => ({ ...p, code: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Unit Label <span className="text-xs text-muted-foreground">(displayed beside quota values)</span></Label>
              <Input
                placeholder="e.g. policies"
                value={rtForm.unit_label}
                onChange={(e) => setRtForm((p) => ({ ...p, unit_label: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Value Type <span className="text-xs text-muted-foreground">(controls UI input & display)</span></Label>
              <Select
                value={rtForm.value_type}
                onValueChange={(v) => setRtForm((p) => ({ ...p, value_type: v as typeof p.value_type }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="count">count — numeric quota with progress bar</SelectItem>
                  <SelectItem value="flag">flag — boolean on/off toggle</SelectItem>
                  <SelectItem value="link">link — URL or string value</SelectItem>
                  <SelectItem value="plugin">plugin — plugin identifier picker</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Description <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input
                placeholder="e.g. Finalized policy documents"
                value={rtForm.description}
                onChange={(e) => setRtForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => { setShowRtForm(false); setRtForm({ code: "", name: "", description: "", unit_label: "", value_type: "count" }); }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveResourceType}
              disabled={rtSaving || !rtForm.code.trim() || !rtForm.name.trim() || !rtForm.unit_label.trim()}
            >
              {rtSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create Resource Type
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Plans</p>
                <p className="text-3xl font-bold mt-1">{stats.total}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Settings className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active</p>
                <p className="text-3xl font-bold mt-1 text-emerald-600">{stats.active}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive</p>
                <p className="text-3xl font-bold mt-1 text-red-500">{stats.inactive}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search plan tiers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              className="pl-9"
            />
          </div>
          <Button onClick={handleSearch} variant="outline" size="icon">
            <Search className="h-4 w-4" />
          </Button>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleFilterChange("is_active", true)}>
              <CheckCircle className="mr-2 h-4 w-4 text-emerald-500" /> Active Plans
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleFilterChange("is_active", false)}>
              <XCircle className="mr-2 h-4 w-4 text-red-400" /> Inactive Plans
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setFilters({})}>
              <Settings className="mr-2 h-4 w-4" /> All Plans
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Plan Tiers Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading plan tiers...</span>
        </div>
      ) : planTiers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Settings className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">No plan tiers found</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Create your first plan to get started</p>
            <Button onClick={() => setShowCreateForm(true)} className="mt-4 gap-2" variant="outline">
              <Plus className="h-4 w-4" /> Create Plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {planTiers.map((plan) => {
            const accent = getTierAccent(plan.tier_level);
            const isExpanded = expandedRows.has(plan.id);
            return (
              <Card
                key={plan.id}
                className={`overflow-hidden transition-all duration-200 hover:shadow-md ${
                  isExpanded ? "ring-1 ring-primary/20 shadow-md" : ""
                }`}
              >
                {/* Card Header Row */}
                <div
                  className="flex items-center gap-4 p-5 cursor-pointer select-none"
                  onClick={() => toggleRowExpansion(plan.id)}
                >
                  {/* Expand Chevron */}
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform" />
                    )}
                  </div>

                  {/* Tier Icon + Name */}
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`h-10 w-10 rounded-lg ${accent.bg} flex items-center justify-center flex-shrink-0`}>
                      {getTierIcon(plan.tier_level)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base">{plan.name}</h3>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${accent.badge}`}>
                          Tier {plan.tier_level}
                        </span>
                      </div>
                      {plan.description && (
                        <p className="text-sm text-muted-foreground mt-0.5 truncate">{plan.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="hidden md:block text-right flex-shrink-0 min-w-[100px]">
                    <div className="text-lg font-bold">
                      {plan.price_monthly != null ? `$${plan.price_monthly}` : "Free"}
                      {plan.price_monthly != null && <span className="text-sm font-normal text-muted-foreground">/mo</span>}
                    </div>
                    {plan.price_yearly != null && (
                      <div className="text-xs text-muted-foreground">${plan.price_yearly}/yr</div>
                    )}
                  </div>

                  {/* Feature Key Count */}
                  <div className="hidden lg:flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">
                      {Object.keys(plan.features).length} feature{Object.keys(plan.features).length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    <Badge variant={plan.is_active ? "default" : "secondary"} className="gap-1">
                      {plan.is_active ? (
                        <><CheckCircle className="h-3 w-3" /> Active</>
                      ) : (
                        <><XCircle className="h-3 w-3" /> Inactive</>
                      )}
                    </Badge>
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setViewingPlan(plan)}>
                          <Eye className="mr-2 h-4 w-4" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setEditingPlan(plan)}>
                          <Edit className="mr-2 h-4 w-4" /> Edit Plan
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeletePlan(plan)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete Plan
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                {isExpanded && (
                  <div className="border-t bg-muted/30">
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                        {/* Plan Details */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Plan Details</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-dashed">
                              <span className="text-sm text-muted-foreground">Name</span>
                              <span className="text-sm font-medium">{plan.name}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-dashed">
                              <span className="text-sm text-muted-foreground">Tier Level</span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${accent.badge}`}>Level {plan.tier_level}</span>
                            </div>
                            {plan.price_monthly != null && (
                              <div className="flex justify-between items-center py-2 border-b border-dashed">
                                <span className="text-sm text-muted-foreground">Monthly</span>
                                <span className="text-sm font-semibold">${plan.price_monthly}/mo</span>
                              </div>
                            )}
                            {plan.price_yearly != null && (
                              <div className="flex justify-between items-center py-2 border-b border-dashed">
                                <span className="text-sm text-muted-foreground">Yearly</span>
                                <span className="text-sm font-semibold">${plan.price_yearly}/yr</span>
                              </div>
                            )}
                          </div>
                          {plan.description && (
                            <div className="bg-background rounded-lg p-3 border">
                              <p className="text-sm text-muted-foreground leading-relaxed">{plan.description}</p>
                            </div>
                          )}
                        </div>

                        {/* Features */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Features</h4>
                          {Object.keys(plan.features).length > 0 ? (
                            <div className="bg-background rounded-lg border p-4 space-y-2">
                              {Object.entries(plan.features as Record<string, unknown>).map(([key, val]) => (
                                <div key={key} className="flex justify-between items-center text-sm py-1 border-b border-dashed last:border-0">
                                  <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" />
                                    <code className="text-xs text-muted-foreground bg-muted px-1 rounded">{key}</code>
                                  </div>
                                  <span className="font-medium">
                                    {typeof val === "boolean" ? (
                                      val ? (
                                        <Badge variant="default" className="text-xs gap-1"><CheckCircle className="h-3 w-3" /> Enabled</Badge>
                                      ) : (
                                        <Badge variant="secondary" className="text-xs gap-1"><XCircle className="h-3 w-3" /> Disabled</Badge>
                                      )
                                    ) : (
                                      formatFeatureValue(val)
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">No features configured</p>
                          )}
                        </div>

                        {/* Status & Metadata */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status & Info</h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center py-2 border-b border-dashed">
                              <span className="text-sm text-muted-foreground">Status</span>
                              <Badge variant={plan.is_active ? "default" : "secondary"} className="gap-1">
                                {plan.is_active ? <><CheckCircle className="h-3 w-3" /> Active</> : <><XCircle className="h-3 w-3" /> Inactive</>}
                              </Badge>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-dashed">
                              <span className="text-sm text-muted-foreground">Created</span>
                              <span className="text-sm">{formatDate(plan.created_at)}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b border-dashed">
                              <span className="text-sm text-muted-foreground">Updated</span>
                              <span className="text-sm">{formatDate(plan.updated_at)}</span>
                            </div>
                          </div>

                          {/* Quick Actions */}
                          <div className="pt-2 flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => setEditingPlan(plan)}>
                              <Edit className="h-3.5 w-3.5" /> Edit
                            </Button>
                            <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => setViewingPlan(plan)}>
                              <Eye className="h-3.5 w-3.5" /> View
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                {((pagination.page || 1) - 1) *
                  (pagination.limit || ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE) + 1}{" "}
                to{" "}
                {Math.min(
                  (pagination.page || 1) * (pagination.limit || ADMIN_CONFIG.PLAN_TIERS.DEFAULT_PAGE_SIZE),
                  totalCount
                )}{" "}
                of {totalCount} results
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  disabled={(pagination.page || 1) <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                >Previous</Button>
                <Button
                  variant="outline" size="sm"
                  disabled={(pagination.page || 1) >= totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                >Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Plan Tier</DialogTitle>
            <DialogDescription>
              Create a new subscription plan tier with custom features and pricing.
            </DialogDescription>
          </DialogHeader>
          <PlanTierForm
            onSave={handlePlanSaved}
            onCancel={() => setShowCreateForm(false)}
            existingPlans={planTiers}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPlan} onOpenChange={(open) => !open && setEditingPlan(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Plan Tier</DialogTitle>
            <DialogDescription>Update the plan tier details, features, and pricing.</DialogDescription>
          </DialogHeader>
          {editingPlan && (
            <PlanTierForm
              planTier={editingPlan}
              onSave={handlePlanSaved}
              onCancel={() => setEditingPlan(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingPlan} onOpenChange={(open) => !open && setViewingPlan(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Plan Tier Details</DialogTitle>
            <DialogDescription>View detailed information about this plan tier.</DialogDescription>
          </DialogHeader>
          {viewingPlan && <PlanTierViewer planTier={viewingPlan} />}
        </DialogContent>
      </Dialog>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AdminSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          pendingUsersCount={pendingUsersCount}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {activeTab === "subscriptions" ? (
              renderSubscriptionManagement()
            ) : activeTab === "addon-packs" ? (
              <AddonPacksManager />
            ) : activeTab === "users" ? (
              <UserManagement />
            ) : activeTab === "roles" ? (
              <RoleManagement />
            ) : activeTab === "teable-config" ? (
              <TeableConfigPage />
            ) : activeTab === "global-integrations" ? (
              <GlobalIntegrationsPage />
            ) : activeTab === "policy-templates" ? (
              <TemplatesSection />
            ) : activeTab === "ai-skills" ? (
              <AiSkillsSection />
            ) : activeTab === "feedback" ? (
              <PolicyFeedbackPage />
            ) : activeTab === "document-register-signups" ? (
              <DocumentRegisterSignupsPage />
            ) : activeTab === "activity-log" ? (
              <ActivityLogPage />
            ) : (
              <AssignBasesPage />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;
